/**
 * AuthCallback — handles the Google-OAuth redirect for BOTH login and
 * signup flows. The `state` param (base64-encoded JSON) carries the
 * mode + next_url so we know which auth-api endpoint to call:
 *
 *   - mode=login (default for legacy startGoogleLogin): /auth/google/callback
 *   - mode=signup (set by startGoogleSignup): /auth/signup/google
 *
 * The signup branch lets the self-signup-bypass kick in for unknown
 * Google emails. After either path completes, we redirect to the
 * next_url from state (or "/").
 */

import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { handleGoogleCallback, handleGoogleSignupCallback } from '../auth'

interface OAuthState {
  mode?: 'login' | 'signup'
  next?: string
}

function decodeState(raw: string | null): OAuthState {
  if (!raw) return {}
  try {
    return JSON.parse(atob(raw))
  } catch {
    return {}
  }
}

export function AuthCallback() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const code = searchParams.get('code')
    if (!code) {
      setError('No authorization code received')
      return
    }
    const oauthState = decodeState(searchParams.get('state'))
    const dest = oauthState.next || '/'

    const promise =
      oauthState.mode === 'signup'
        ? handleGoogleSignupCallback(code)
        : handleGoogleCallback(code)

    promise
      .then(() => navigate(dest, { replace: true }))
      .catch((err) => setError(err.message))
  }, [searchParams, navigate])

  if (error) {
    return (
      <div className="login-page">
        <div className="login-card">
          <div className="login-logo">Content</div>
          <p style={{ color: 'var(--accent)', marginBottom: 16 }}>{error}</p>
          <button className="login-google-btn" onClick={() => navigate('/login')}>
            Zurück zum Login
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">Content</div>
        <p className="login-subtitle">Anmeldung wird verarbeitet...</p>
      </div>
    </div>
  )
}
